import React, { useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  useDesignFormStore,
  ImageDetails,
} from "../../store/DesignMasterStore";
import { Upload, File as FileIcon, X } from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

// Reusable component for a single file upload area
const ImageUploader: React.FC<{
  onDrop: (acceptedFiles: File[]) => void;
  file: ImageDetails | File | null;
  onRemove: () => void;
  title?: string;
  description?: string;
}> = ({ onDrop, file, onRemove, title, description }) => {
  // const { getRootProps, getInputProps, isDragActive } = useDropzone({
  //     onDrop,
  //     accept: { 'image/jpeg': [], 'image/png': [] },
  //     multiple: false,
  //     maxSize: 10 * 1024 * 1024,
  // });
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (fileRejections) => {
      fileRejections.forEach((rejection) => {
        const file = rejection.file; // rejected file
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2); // MB me convert

        rejection.errors.forEach((e) => {
          if (e.code === "file-too-large") {
            toast.error(` File is ${fileSizeMB}MB. Max allowed size is 10MB.`);
          }
          if (e.code === "file-invalid-type") {
            toast.error(
              ` ${file.name} is not a supported format. Only JPG/PNG allowed.`
            );
          }
        });
      });
    },
    accept: { "image/jpeg": [], "image/png": [] },
    multiple: false,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const [previewSrc, setPreviewSrc] = React.useState<string | null>(null);
  const blobUrlRef = React.useRef<string | null>(null);
  const fileRef = React.useRef<File | null>(null);

  useEffect(() => {
    // Case 1: If file is a new upload (File object)
    if (file instanceof File) {
      // Only create new blob URL if file actually changed
      if (fileRef.current !== file) {
        // Clean up previous blob URL if it exists
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        // Create new blob URL
        const url = URL.createObjectURL(file);
        blobUrlRef.current = url;
        fileRef.current = file;
        setPreviewSrc(url);
      } else if (blobUrlRef.current) {
        // File hasn't changed, keep using existing blob URL
        setPreviewSrc(blobUrlRef.current);
      }
    }
    // Case 2: If file is from backend (ImageDetails object)
    else if (file && typeof file === "object" && "url" in file) {
      // Clean up blob URL if switching from File to ImageDetails
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
        fileRef.current = null;
      }
      setPreviewSrc(file.url);
    }
    // Case 3: If file is null
    else {
      // Clean up blob URL
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
        fileRef.current = null;
      }
      setPreviewSrc(null);
    }

    // Cleanup function to revoke blob URL when component unmounts
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
        fileRef.current = null;
      }
    };
  }, [file]);

  const fileName =
    file instanceof File ? file.name : file?.originalName || "Image";

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${
              isDragActive
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 hover:border-gray-400"
            }`}
    >
      <input {...getInputProps()} />
      {previewSrc ? (
        <div className="relative">
          <div className="flex flex-col items-center gap-2 text-gray-700">
            <img
              src={previewSrc}
              alt="Preview"
              className="max-h-40 mx-auto rounded-lg object-contain"
              onError={(e) => {
                console.error("Image preview error:", e);
                // If blob URL fails, try to recreate it if it's a File
                if (file instanceof File && blobUrlRef.current) {
                  // Blob URL might have been revoked, recreate it
                  try {
                    URL.revokeObjectURL(blobUrlRef.current);
                  } catch (err) {
                    // Ignore if already revoked
                  }
                  const newUrl = URL.createObjectURL(file);
                  blobUrlRef.current = newUrl;
                  setPreviewSrc(newUrl);
                }
              }}
            />
            <span className="text-sm font-medium truncate max-w-full">
              {fileName}
            </span>
          </div>
          <button
            type="button"
            aria-label="Remove image"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 text-gray-500">
          <Upload className="w-10 h-10" />
          {title && (
            <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          )}
          {description && <p className="text-xs">{description}</p>}
          <span className="mt-2 text-xs font-medium text-blue-600">
            Choose File
          </span>
        </div>
      )}
    </div>
  );
};

const ImagesTab: React.FC = () => {
  const { images, setPrimaryImage, updateAdditionalImage } =
    useDesignFormStore();

  const onPrimaryDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setPrimaryImage(acceptedFiles[0]);
      }
    },
    [setPrimaryImage]
  );

  const onAdditionalDrop = useCallback(
    (index: number, acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        updateAdditionalImage(index, acceptedFiles[0]);
      }
    },
    [updateAdditionalImage]
  );

  return (
    <div className="bg-white p-6 rounded-xl">
      <h2 className="text-xl font-bold text-gray-800 mb-6">Image Management</h2>

      {/* Primary Image Uploader */}
      <div className="mb-8">
        <ImageUploader
          onDrop={onPrimaryDrop}
          file={images.primaryImage}
          onRemove={() => setPrimaryImage(null)}
          title="Upload Primary Design Image"
          description="Drag and drop or click to browse"
        />
        <p className="mt-2 text-center text-xs text-gray-500">
          Supports JPG, PNG formats. Max size: 10MB
        </p>
      </div>

      {/* Additional Views */}
      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">
          Additional Views (Optional)
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <ImageUploader
            onDrop={(files) => onAdditionalDrop(1, files)}
            file={images.additionalImage1}
            onRemove={() => updateAdditionalImage(1, null)}
          />
          <ImageUploader
            onDrop={(files) => onAdditionalDrop(2, files)}
            file={images.additionalImage2}
            onRemove={() => updateAdditionalImage(2, null)}
          />
          <ImageUploader
            onDrop={(files) => onAdditionalDrop(3, files)}
            file={images.additionalImage3}
            onRemove={() => updateAdditionalImage(3, null)}
          />
          <ImageUploader
            onDrop={(files) => onAdditionalDrop(4, files)}
            file={images.additionalImage4}
            onRemove={() => updateAdditionalImage(4, null)}
          />
        </div>
      </div>
    </div>
  );
};

export default ImagesTab;
